// PWA service-worker registration for the CoraFlow patient portal.
//
// vite-plugin-pwa exposes the SW + its registration helper through the virtual
// module `virtual:pwa-register`. We register manually (rather than letting the
// plugin auto-inject) so we can:
//   1. Gate registration to PRODUCTION builds only — a no-op in dev/test so it
//      never interferes with Vite HMR or vitest (jsdom has no SW anyway).
//   2. Reuse the EXISTING version-poll reload banner: when a new SW is waiting,
//      we surface the same `update.*` prompt instead of a second mechanism.
//
// `registerType: 'prompt'` (configured in vite.config.ts) means the new SW
// waits for an explicit `updateSW(true)` call before activating — which we
// trigger from the banner's "reload now" button via signalServiceWorkerUpdate.

import { registerServiceWorkerUpdateChecker, signalServiceWorkerUpdate } from '@/composables/useVersionCheck';

export function registerServiceWorker(): void {
  // No-op outside production builds: keeps Vite HMR and vitest untouched.
  // import.meta.env.PROD is statically false in dev/test, so the dynamic
  // import below is tree-shaken out of those bundles entirely.
  if (!import.meta.env.PROD) return;
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

  // Dynamic import so the virtual module is only pulled into the prod bundle.
  void import('virtual:pwa-register').then(({ registerSW }) => {
    const updateSW = registerSW({
      // We drive activation from the user-facing reload prompt, not eagerly.
      immediate: false,
      onRegisteredSW(_swScriptUrl, registration) {
        if (!registration) return;
        // The browser only re-checks sw.js on a navigation, while the version
        // poll spots a deploy within two minutes — so hand the poll a way to
        // start the worker update itself. Without it the banner's first
        // "reload now" is served the old build from this worker's precache.
        registerServiceWorkerUpdateChecker(() => {
          void registration.update();
        });
      },
      onNeedRefresh() {
        // A new worker is waiting. Feed the existing reload banner; its
        // "reload now" action calls back into updateSW(true) to skip waiting
        // and reload once the new SW takes control.
        signalServiceWorkerUpdate(() => {
          void updateSW(true);
        });
      },
    });
  });
}
